import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'creditCardRadio',
    'creditCardFields',
    'cardNumber',
    'cardExpiry',
    'cardCvv',
    'cardHolder',
    'cardToken',
    'submitButton',
    'errorMessage',
    'buyerCpf',
  ];
  static values = { pagarmePublicKey: String };

  connect() {
    this.toggleFields();
    this.setupCardFormatting();
    this.setupCpfFormatting();
  }

  toggleFields() {
    const showCC = this.hasCreditCardRadioTarget && this.creditCardRadioTarget.checked;
    this.creditCardFieldsTarget.classList.toggle('hidden', !showCC);
  }

  async submit(event) {
    if (!this.hasCreditCardRadioTarget || !this.creditCardRadioTarget.checked) return;

    event.preventDefault();
    this.clearError();

    const button = this.submitButtonTarget;
    const originalValue = button.value;
    button.disabled = true;
    button.value = 'Processando...';

    try {
      await this.tokenizeCard();
      this.element.submit();
    } catch (error) {
      button.disabled = false;
      button.value = originalValue;
      this.showError(error.message || 'Erro ao processar cartão. Verifique os dados e tente novamente.');
    }
  }

  async tokenizeCard() {
    const expiry = this.cardExpiryTarget.value.replace(/\s/g, '');
    const [rawMonth, rawYear] = expiry.split('/');
    const expMonth = parseInt(rawMonth, 10);
    const expYear = rawYear.length === 2 ? 2000 + parseInt(rawYear, 10) : parseInt(rawYear, 10);

    const response = await fetch(
      `https://api.pagar.me/core/v5/tokens?appId=${this.pagarmePublicKeyValue}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: this.cardNumberTarget.value.replace(/\D/g, ''),
            holder_name: this.cardHolderTarget.value.trim(),
            exp_month: expMonth,
            exp_year: expYear,
            cvv: this.cardCvvTarget.value.trim(),
          },
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.id) {
      throw new Error(this.translateCardError(data));
    }

    this.cardTokenTarget.value = data.id;
  }

  setupCardFormatting() {
    if (this.hasCardNumberTarget) {
      this.cardNumberTarget.addEventListener('input', (e) => {
        let v = e.target.value.replace(/\D/g, '').slice(0, 16);
        e.target.value = v.replace(/(.{4})/g, '$1 ').trim();
      });
    }

    if (this.hasCardExpiryTarget) {
      this.cardExpiryTarget.addEventListener('input', (e) => {
        let v = e.target.value.replace(/\D/g, '').slice(0, 6);
        if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2);
        e.target.value = v;
      });
    }

    if (this.hasCardCvvTarget) {
      this.cardCvvTarget.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, 4);
      });
    }
  }

  setupCpfFormatting() {
    if (!this.hasBuyerCpfTarget) return;

    this.buyerCpfTarget.addEventListener('input', (event) => {
      const digits = event.target.value.replace(/\D/g, '').slice(0, 11);
      const parts = [];

      if (digits.length > 0) parts.push(digits.slice(0, 3));
      if (digits.length > 3) parts.push(digits.slice(3, 6));
      if (digits.length > 6) parts.push(digits.slice(6, 9));

      event.target.value = parts.join('.') + (digits.length > 9 ? `-${digits.slice(9)}` : '');
    });
  }

  translateCardError(data) {
    const fieldLabels = {
      'card.number': 'número do cartão',
      'card.holder_name': 'nome no cartão',
      'card.exp_month': 'mês de vencimento',
      'card.exp_year': 'ano de vencimento',
      'card.cvv': 'CVV',
    };

    const errors = data?.errors;
    if (errors && typeof errors === 'object') {
      const fields = Object.keys(errors).map((key) => fieldLabels[key] || key).join(', ');
      return `Verifique o(s) campo(s): ${fields}.`;
    }

    return 'Dados do cartão inválidos. Verifique as informações e tente novamente.';
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.classList.remove('hidden');
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = '';
      this.errorMessageTarget.classList.add('hidden');
    }
  }
}
